import './index.scss';

import React from 'react';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';

import HelloModal from 'common/components/hello-modal';
import Button from 'common/components/button';
import AdminSetting from 'hellospa/components/admin-setting';
import { AdminSettingsSimpleRow } from 'hellospa/components/admin-settings-row';
import AdminSettingsRowGroup from 'hellospa/components/admin-settings-row-group';
import hsFetch from 'hellospa/hs-fetch';
import SwitchBillingPeriodModal from 'hellospa/components/switch-billing-period-modal';
import { trackHeapCustomEvent } from 'common/utils/heap';
import VatRules from 'common/constants/vat-rules';
import TaxId from 'common/components/tax-id';
import countries from 'common/constants/countries';

import Plan from './plan';
import BillingCycle from './billing-cycle';
import BillingInfo from './billing-info';
import AddonInfo from './addon-info';
import Invoices from './invoices';
import ReceiptAddress from './receipt-address';

const messages = defineMessages({
  plan: {
    id: '',
    description: 'Header for a the subscription plan.',
    defaultMessage: 'Plan',
  },
  plans: {
    id: '',
    description: 'Header for a list of subscription plans.',
    defaultMessage: 'Plans',
  },
  cancelPlan: {
    id: '',
    description: 'Text for a button which allows the user to cancel subscription plan when pressed.',
    defaultMessage: 'Cancel plan',
  },
  resumePlan: {
    id: '',
    description: 'Text for a button which allows the user to resume a canceled subscription plan when pressed.',
    defaultMessage: 'Resume plan',
  },
  changePlan: {
    id: '',
    description: 'Text for a button which allows the user to change their subscription plan when pressed.',
    defaultMessage: 'Change plan',
  },
  switchToAnnual: {
    id: '',
    description: 'Text for a button which allows the user to change their subscription to annual billing when pressed.',
    defaultMessage: 'Switch to annual',
  },
  switchToMonthly: {
    id: '',
    description: 'Text for a button which allows the user to change their subscription to monthly billing when pressed.',
    defaultMessage: 'Switch to monthly',
  },
  billingCycle: {
    id: '',
    description: 'Header for a section with information related to a user\'s billing cycle.',
    defaultMessage: 'Billing cycle',
  },
  billingInformation: {
    id: '',
    description: 'Header for a section with information related to a user\'s billing information.',
    defaultMessage: 'Billing information',
  },
  billingReceipts: {
    id: '',
    description: 'Header for a section with information related to a user\'s billing receipts.',
    defaultMessage: 'Billing receipts',
  },
  addBilling: {
    id: '',
    description: 'Text for a button which allows the user to add a credit card to their account when pressed.',
    defaultMessage: 'Add credit card',
  },
  updateBilling: {
    id: '',
    description: 'Text for a button which allows the user to update their credit card information on their account.',
    defaultMessage: 'Update',
  },
  receiptAddress: {
    id: '',
    description: 'Header for a section with information related to a user\'s receipt address a text input a user can type an address into.',
    defaultMessage: 'Receipt address',
  },
  nevermind: {
    id: '',
    description: 'Text for a button which cancels an action when pressed.',
    defaultMessage: 'Nevermind',
  },
  removeCard: {
    id: '',
    description: 'Text for a button which removes the credit card from the user\'s account when pressed.',
    defaultMessage: 'Remove card',
  },
  switchBillingCycle: {
    id: '',
    description: 'Text for a button which switches the user\'s billing cycle from monthly to annual when pressed.',
    defaultMessage: 'Switch billing cycle',
  },
  saveButton: {
    id: '',
    description: 'Text for a button which saves the users address they inputed into a text area',
    defaultMessage: 'Save',
  },
  editAddressButton: {
    id: '',
    description: 'Text for a button which allows the user to edit address they inputed into a text area',
    defaultMessage: 'Edit Address',
  },
  receiptTooltip: {
    id: '',
    description: 'tooltip content that appears over the label on a section telling user to ender an address or content in a textarea',
    defaultMessage: 'Information that will appear on your receipt',
  },
  addonInformation: {
    id: '',
    description: 'Header for a section with information related to a user\'s add-on usage.',
    defaultMessage: 'Add-Ons',
  },
  contactUsAddon: {
    id: '',
    description: 'Text for a button which allows the user to reach a contact form for Sales when pressed.',
    defaultMessage: 'Contact us to update',
  },
  // messaging for all tax ids will display as "VAT number" unless the users country is canada
  taxIdTitle: {
    id: '',
    description: 'header of a section where the user can see their tax ID/VAT number',
    defaultMessage: '{taxCountry, select, CA {Tax ID} other {VAT number}}',
  },
  updateTaxId: {
    id: '',
    description: 'title of a modal when a user is updating their tax ID/VAT number',
    defaultMessage: 'Update {taxCountry, select, CA {Tax ID} other {VAT number}}',
  },
  addTaxId: {
    id: '',
    description: 'text of a button allowing the user to add their tax ID/VAT number',
    defaultMessage: 'Add {taxCountry, select, CA {Tax ID} other {VAT number}}',
  },
  invalidTaxId: {
    id: '',
    description: 'error message when something goes wrong when trying to save a customers tax id number with examples of expected input',
    defaultMessage: 'Please enter a valid {taxCountry, select, CA {Tax ID} other {VAT number}} (ex: {example})',
  },
  editButton: {
    id: '',
    description: 'text of a button allowing the user to edit a field',
    defaultMessage: 'Edit',
  },
  cancelButton: {
    id: '',
    description: 'text of a button allowing the user to cancel their current action',
    defaultMessage: 'Cancel',
  },
});

class BillingSettings extends React.PureComponent {

  state = {
    isRemovingCard: false,
    showRemoveCardModal: false,
    isSwitchingBillingCycle: false,
    showSwitchBillingPeriodModal: false,
    receiptAddressReadOnly: true,
    addressSavedMessage: '',
    receiptAddressValue: '',
    taxId: '',
    taxIdValue: this.props.taxId,
    taxIdSaving: false,
    taxIdErrorMessage: '',
  };

  hideRemoveCardModal = () => {
    this.setState({
      showRemoveCardModal: false,
    });
  };

  showRemoveCardModal = (evt) => {
    evt.preventDefault();

    this.setState({
      showRemoveCardModal: true,
    });
  };

  hideTaxIdModal = () => {
    this.setState({
      showTaxIdModal: false,
    });
  };

  showTaxIdModal = (evt) => {
    evt.preventDefault();

    this.setState({
      showTaxIdModal: true,
      taxIdErrorMessage: '',
    });
  };

  showSwitchBillingPeriodModal = (evt) => {
    evt.preventDefault();

    this.setState({
      showSwitchBillingPeriodModal: true,
    });
  };

  onSwitchBillingPeriodModalRequestClose = () => {
    this.setState({
      showSwitchBillingPeriodModal: false,
    });
  };

  onSwitchBillingPeriodSuccess = () => {
    this.setState({
      showSwitchBillingPeriodModal: false,
    });

    if (this.props.isMonthly) {
      trackHeapCustomEvent('switch_to_annual_billing_settings');
    } else {
      trackHeapCustomEvent('switch_to_monthly_billing_settings');
    }

    // Reload page.
    document.location.reload();
  };

  onRemoveCardModalRequestClose = () => {
    this.hideRemoveCardModal();
  };

  onTaxIdModalRequestClose = () => {
    this.hideTaxIdModal();
  };

  onClickTaxIdSave = () => {
    const { intl, taxCountry } = this.props;
    this.setState({
      taxIdSaving: true,
      taxIdErrorMessage: '',
    });

    hsFetch('/endpoint/account/updateTaxId', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        csrf_token: this.props.app.csrfToken,
        country: taxCountry,
        taxId: this.state.taxId,
      }),
    }).then((response) => {
      response.json().then((obj) => {
        if (response.status !== 200) {
          this.setState({
            taxIdErrorMessage: intl.formatMessage(messages.invalidTaxId, {
              taxCountry,
              example: taxCountry === 'CA' ? 'GST, HST, QST' : VatRules[taxCountry].examples.join(', '),
            }),
          });
        } else {
          this.setState({
            taxId: '',
            taxIdValue: obj.value,
          });
          this.onTaxIdModalRequestClose();
        }

        this.setState({ taxIdSaving: false });
      });
    });
  };

  onRemoveCard = () => {
    this.setState({ isRemovingCard: true });

    const formData = new FormData();

    formData.append('csrf_token', this.props.app.csrfToken);

    hsFetch('/account/removeCard', {
      method: 'POST',
      body: formData,
    }).then((_response) => {
      this.setState({ isRemovingCard: false });

      window.location.reload();
    });
  };

  onClickReceiptAddressSave = () => {
    if (this.state.receiptAddressReadOnly) {
      this.setState({
        receiptAddressReadOnly: false,
      });
    } else {
      this.setState({
        addressSavedMessage: <FormattedMessage
          id=""
          description="tells user that their data is being saved after clicking the save button"
          defaultMessage="Saving..." />,
      });

      hsFetch('/account/updateReceiptAddress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          csrf_token: this.props.app.csrfToken,
          receipt_address: this.state.receiptAddressValue,
        }),
      }).then((response) => {
        response.json().then((obj) => {
          this.setState({
            addressSavedMessage: obj.message,
            receiptAddressValue: obj.value,
            receiptAddressReadOnly: true,
          });
        });
      });
    }
  };

  onChangeReceiptAddress = (evt) => {
    this.setState({
      receiptAddressValue: evt.target.value,
    });
    this.setState({
      addressSavedMessage: <FormattedMessage
        id=""
        description="Text under a text area that tells user their edits have not been saved"
        defaultMessage="These changes have not been saved" />,
    });
  };

  onChangeTaxId = (taxId) => {
    this.setState({ taxId });
  };

  render() {
    const {
      hasSubscriptionAccess,
      billingPlans,
      // canCancel,
      numSeats,
      numTrialDays,
      teamSize,
      startDate,
      isHf,
      isFreeTrial,
      isFree,
      localizedTotalCharge,
      localizedTotalChargeWithDiscounts,
      localizedPeriodName,
      numTemplates,
      numTemplatesLeft,
      nextBillingDate,
      renewDate,
      isEligibleToSwitchBillingPeriodInWebApp,
      isMonthly,
      isDelinquent,
      isBillingInfoFake,
      hasBillingInfo,
      hasAlternativeBilling,
      hasBillingInfoAccess,
      isWebPaid,
      lastChargeDate,
      hasAPIBillingPlan,
      isAPIPaid,
      hasWebCoupon,
      webCouponPercentage,
      hasAPICoupon,
      apiCouponPercentage,
      billingPeriodsLeft,
      apiBillingPeriodsLeft,
      ccSuffix,
      ccExpMonth,
      ccExpYear,
      intl,
      hasTeam,
      isOrgAdmin,
      resumeLinkUi,
      resumeLinkApi,
      subEndDate,
      subApiEndDate,
      hfNumFaxPagesPerMonth,
      hfNumFaxPagesLeft,
      hfNumBonusFaxPages,
      hfInviteURL,
      hfIsPaid,
      hfNumExtraPages,
      hfPromoName,
      taxIdSupported,
      taxCountry,
      taxId,
      receiptAddress,
      altBillingPeriodCharge,
      altBillingPeriodStartDate,
      altBillingPeriodRefund,
      altBillingPeriodError,
      addonSalesforceUsage,
      addonSalesforceQuota,
      addonOracleCPQUsage,
      addonOracleCPQQuota,
      addonQESUsage,
      addonQESQuota,
      addonDisplayNames,
      addonSmsDeliveryQuota,
      addonSmsDeliveryUsage,
      isAutoDowngrade,
      allowManagePlan,

      // SMS quota
      hasSignerSMSAuth,
      hasUnlimitedSMSAuth,
      signerSMSAuthenticationTotal,
      signerSMSAuthenticationUsed,
      signerSMSAuthenticationLeft,

      // Blueberry
      plan,
      currentPeriod,
      proratio,

      // Tax collection
      isTaxCollectionEnabled,
      ...rest
    } = this.props;

    const { taxIdValue } = this.state;

    let taxIdButtonText;
    if (taxIdValue) {
      taxIdButtonText = intl.formatMessage(messages.editButton);
    } else {
      taxIdButtonText = intl.formatMessage(messages.addTaxId, { taxCountry });
    }

    return (
      <div className="billing-settings">
        {(isHf || hasSubscriptionAccess) && (
          <React.Fragment>
            <AdminSettingsRowGroup flush>
              {() => (
                (isHf && !billingPlans) ? (
                  <AdminSettingsSimpleRow
                    title={intl.formatMessage(messages.plan)}
                    isPrimary
                  >
                    <AdminSetting className="billing-settings__billing-plan">
                      <Plan
                        allowManagePlan={allowManagePlan}
                        isBillingInfoFake={isBillingInfoFake}
                        hasAlternativeBilling={hasAlternativeBilling}
                        numSeats={numSeats}
                        numTrialDays={numTrialDays}
                        teamSize={teamSize}
                        startDate={startDate}
                        isHf={isHf}
                        isFree={isFree}
                        isFreeTrial={isFreeTrial}
                        numTemplates={numTemplates}
                        numTemplatesLeft={numTemplatesLeft}
                        subEndDate={subEndDate}
                        subApiEndDate={subApiEndDate}
                        hfNumFaxPagesPerMonth={hfNumFaxPagesPerMonth}
                        hfNumFaxPagesLeft={hfNumFaxPagesLeft}
                        hfNumBonusFaxPages={hfNumBonusFaxPages}
                        hfInviteURL={hfInviteURL}
                        hfIsPaid={hfIsPaid}
                        hfNumExtraPages={hfNumExtraPages}
                        hfPromoName={hfPromoName} />
                    </AdminSetting>
                  </AdminSettingsSimpleRow>
                ) : (
                  <AdminSettingsSimpleRow
                    title={
                      intl.formatMessage(billingPlans.length > 1 ? messages.plans : messages.plan)
                    }
                    isPrimary
                  >
                    {billingPlans.map((bpq, i) => (
                      <AdminSetting
                        className="billing-settings__billing-plan"
                        key={`${i}-${bpq.name}`}
                        customCol={
                          <React.Fragment>
                            {/* eslint-disable-next-line max-len */}
                            {(!isFree && !isDelinquent && ((bpq.isApi && !!subApiEndDate) || (!bpq.isApi && !!subEndDate))) && (
                              // If the user is not delinquent, and the current
                              // plan's end date is not null, then show the
                              // the "Resume" button.
                              <Button
                                data-qa-ref="resume-plan-btn"
                                buttonColor="white"
                                buttonBorderColor="white"
                                buttonTextColor="castle-rock"
                                buttonHoverTextColor="castle-rock"
                                buttonHoverColor="warm-chinchilla"
                                buttonHoverBorderColor="warm-chinchilla"
                                buttonSize="sm"
                                buttonLink={bpq.isApi ? resumeLinkApi : resumeLinkUi}
                                buttonText={intl.formatMessage(messages.resumePlan)} />
                            )}
                            {/* eslint-disable-next-line max-len */}
                            {(!isFree && ((bpq.isApi && !subApiEndDate) || (!bpq.isApi && !subEndDate))) && (
                              <React.Fragment>
                                {((bpq.isApi && isAPIPaid) || (!bpq.isApi && isWebPaid)) && (<Button
                                  data-qa-ref="cancel-plan-btn"
                                  buttonColor="white"
                                  buttonBorderColor="white"
                                  buttonTextColor="castle-rock"
                                  buttonHoverTextColor="castle-rock"
                                  buttonHoverColor="warm-chinchilla"
                                  buttonHoverBorderColor="warm-chinchilla"
                                  buttonSize="sm"
                                  buttonLink={`/account/cancelSubscription?p=${bpq.isApi ? 'api' : 'ui'}`}
                                  buttonText={intl.formatMessage(messages.cancelPlan)} />)}
                                <Button
                                  data-qa-ref="change-plan-btn"
                                  buttonClass="billing-settings__col-button"
                                  buttonSize="sm"
                                  buttonColor="white"
                                  buttonBorderColor="ocean-blue"
                                  buttonTextColor="ocean-blue"
                                  buttonHoverColor="ocean-blue"
                                  buttonHoverBorderColor="ocean-blue"
                                  buttonLink={bpq.changePlanLink}
                                  buttonText={intl.formatMessage(messages.changePlan)} />
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        }
                      >
                        <Plan
                          {...bpq}
                          allowManagePlan={allowManagePlan}
                          isBillingInfoFake={isBillingInfoFake}
                          hasAlternativeBilling={hasAlternativeBilling}
                          numSeats={numSeats}
                          numTrialDays={numTrialDays}
                          teamSize={teamSize}
                          startDate={startDate}
                          isHf={isHf}
                          isFree={isFree}
                          isFreeTrial={isFreeTrial}
                          numTemplates={numTemplates}
                          numTemplatesLeft={numTemplatesLeft}
                          subEndDate={subEndDate}
                          subApiEndDate={subApiEndDate}
                          hfNumFaxPagesPerMonth={hfNumFaxPagesPerMonth}
                          hfNumFaxPagesLeft={hfNumFaxPagesLeft}
                          hfNumBonusFaxPages={hfNumBonusFaxPages}
                          hfInviteURL={hfInviteURL}
                          hfIsPaid={hfIsPaid}
                          hfNumExtraPages={hfNumExtraPages}
                          hfPromoName={hfPromoName}
                          hasSignerSMSAuth={hasSignerSMSAuth && !addonDisplayNames.addon_smstools}
                          hasUnlimitedSMSAuth={hasUnlimitedSMSAuth}
                          signerSMSAuthenticationTotal={signerSMSAuthenticationTotal}
                          signerSMSAuthenticationUsed={signerSMSAuthenticationUsed}
                          signerSMSAuthenticationLeft={signerSMSAuthenticationLeft} />
                      </AdminSetting>
                    ))}
                  </AdminSettingsSimpleRow>
                )
              )}
            </AdminSettingsRowGroup>
            {hasSubscriptionAccess && (
              <React.Fragment>
                <AdminSettingsRowGroup flush>
                  {() => (
                    <AdminSettingsSimpleRow
                      title={intl.formatMessage(messages.addonInformation)}
                      isPrimary
                    >
                      <AdminSetting
                        customCol={
                        (<Button
                          data-qa-ref="contact-us-addon-btn"
                          buttonColor="white"
                          buttonBorderColor="white"
                          buttonTextColor="ocean-blue"
                          buttonHoverTextColor="ocean-blue"
                          buttonHoverColor="white"
                          buttonHoverBorderColor="white"
                          buttonSize="sm"
                          buttonLink={'https://www.hellosign.com/form/contact-us-general'}
                          buttonText={intl.formatMessage(messages.contactUsAddon)} />)
                      }
                      >
                        <AddonInfo
                          addonSalesforceUsage={addonSalesforceUsage}
                          addonSalesforceQuota={addonSalesforceQuota}
                          addonOracleCPQUsage={addonOracleCPQUsage}
                          addonOracleCPQQuota={addonOracleCPQQuota}
                          addonQESUsage={addonQESUsage}
                          addonQESQuota={addonQESQuota}
                          addonDisplayNames={addonDisplayNames}
                          addonSmsDeliveryQuota={addonSmsDeliveryQuota}
                          addonSmsDeliveryUsage={addonSmsDeliveryUsage} />
                      </AdminSetting>
                    </AdminSettingsSimpleRow>
                  )}
                </AdminSettingsRowGroup>
              </React.Fragment>
            )}
            {hasSubscriptionAccess && (
              <AdminSettingsRowGroup flush>
                {() => (
                  <AdminSettingsSimpleRow
                    title={intl.formatMessage(messages.billingCycle)}
                    isPrimary
                  >
                    <AdminSetting
                      customCol={
                        isEligibleToSwitchBillingPeriodInWebApp && (
                          <Button
                            data-qa-ref="switch-billing-period"
                            disabled={this.state.isSwitchingBillingCycle}
                            onClick={this.showSwitchBillingPeriodModal}
                            buttonSize="sm"
                            buttonColor="white"
                            buttonBorderColor="ocean-blue"
                            buttonTextColor="ocean-blue"
                            buttonHoverColor="ocean-blue"
                            buttonHoverBorderColor="ocean-blue"
                            buttonText={
                              isMonthly
                                ? intl.formatMessage(messages.switchToAnnual)
                                : intl.formatMessage(messages.switchToMonthly)} />
                        )
                      }
                    >
                      <BillingCycle
                        billingPlans={billingPlans || []}
                        isFreeTrial={isFreeTrial}
                        localizedPeriodName={localizedPeriodName}
                        localizedTotalCharge={localizedTotalCharge}
                        localizedTotalChargeWithDiscounts={localizedTotalChargeWithDiscounts}
                        startDate={startDate}
                        isFree={isFree}
                        nextBillingDate={nextBillingDate}
                        renewDate={renewDate}
                        hasWebCoupon={hasWebCoupon}
                        webCouponPercentage={webCouponPercentage}
                        hasAPICoupon={hasAPICoupon}
                        apiCouponPercentage={apiCouponPercentage}
                        billingPeriodsLeft={billingPeriodsLeft}
                        apiBillingPeriodsLeft={apiBillingPeriodsLeft}
                        hasAlternativeBilling={hasAlternativeBilling}
                        isDelinquent={isDelinquent}
                        subEndDate={subEndDate}
                        subApiEndDate={subApiEndDate}
                        isAutoDowngrade={isAutoDowngrade}
                        isTaxCollectionEnabled={isTaxCollectionEnabled} />
                    </AdminSetting>
                  </AdminSettingsSimpleRow>
                )}
              </AdminSettingsRowGroup>
            )}
          </React.Fragment>
        )}
        {hasBillingInfoAccess && (
          <React.Fragment>
            <AdminSettingsRowGroup flush>
              {() => (
                <AdminSettingsSimpleRow
                  title={intl.formatMessage(messages.billingInformation)}
                  isPrimary
                >
                  <AdminSetting
                    customCol={
                      <React.Fragment>
                        {hasBillingInfo && isFree && !isBillingInfoFake && (
                          <Button
                            onClick={this.showRemoveCardModal}
                            data-qa-ref="remove-card-btn"
                            buttonColor="white"
                            buttonBorderColor="white"
                            buttonTextColor="castle-rock"
                            buttonHoverTextColor="castle-rock"
                            buttonHoverColor="warm-chinchilla"
                            buttonHoverBorderColor="warm-chinchilla"
                            buttonSize="sm"
                            buttonText={intl.formatMessage(messages.removeCard)} />
                        )}
                        <Button
                          data-qa-ref="update-card"
                          buttonClass="billing-settings__col-button"
                          buttonLink="/account/updateCard"
                          buttonSize="sm"
                          buttonColor="white"
                          buttonBorderColor="ocean-blue"
                          buttonTextColor="ocean-blue"
                          buttonHoverColor="ocean-blue"
                          buttonHoverBorderColor="ocean-blue"
                          buttonText={hasBillingInfo && !isBillingInfoFake
                            ? intl.formatMessage(messages.updateBilling)
                            : intl.formatMessage(messages.addBilling)} />
                      </React.Fragment>
                    }
                  >
                    <BillingInfo
                      hasBillingInfo={hasBillingInfo}
                      billingPlans={billingPlans || []}
                      isBillingInfoFake={isBillingInfoFake}
                      hasAPIBillingPlan={hasAPIBillingPlan}
                      hasTeam={hasTeam}
                      isOrgAdmin={isOrgAdmin}
                      ccSuffix={ccSuffix}
                      ccExpMonth={ccExpMonth}
                      ccExpYear={ccExpYear}
                      isDelinquent={isDelinquent}
                      taxCountry={taxCountry} />
                  </AdminSetting>
                </AdminSettingsSimpleRow>
              )}
            </AdminSettingsRowGroup>
            { taxIdSupported && (
              <AdminSettingsRowGroup flush>
                {() => (
                  <AdminSettingsSimpleRow
                    title={intl.formatMessage(messages.taxIdTitle, { taxCountry })}
                    isPrimary
                  >
                    <AdminSetting
                      customCol={
                        <React.Fragment>
                          <Button
                            onClick={this.showTaxIdModal}
                            data-qa-ref="update-tax-id-btn"
                            buttonClass="billing-settings__col-button"
                            buttonSize="sm"
                            buttonColor="white"
                            buttonBorderColor="ocean-blue"
                            buttonTextColor="ocean-blue"
                            buttonHoverColor="ocean-blue"
                            buttonHoverBorderColor="ocean-blue"
                            buttonText={taxIdButtonText} />
                        </React.Fragment>
                      }
                    >
                      {this.state.taxIdValue}
                    </AdminSetting>
                  </AdminSettingsSimpleRow>
                )}
              </AdminSettingsRowGroup>
            )}
            <AdminSettingsRowGroup flush bordlerless>
              {() => (
                <AdminSettingsSimpleRow
                  title={intl.formatMessage(messages.billingReceipts)}
                  isPrimary
                >
                  <AdminSetting>
                    <Invoices
                      isWebPaid={isWebPaid}
                      lastChargeDate={lastChargeDate}
                      hasAPIBillingPlan={hasAPIBillingPlan}
                      isAPIPaid={isAPIPaid} />
                  </AdminSetting>
                </AdminSettingsSimpleRow>
              )}
            </AdminSettingsRowGroup>
            <AdminSettingsRowGroup flush bordlerless>
              {() => (
                <AdminSettingsSimpleRow
                  title={intl.formatMessage(messages.receiptAddress)}
                  tooltip={intl.formatMessage(messages.receiptTooltip)}
                  isPrimary
                  id='receipt-memo'
                >
                  <AdminSetting customCol={
                    <React.Fragment>
                      <Button
                        data-qa-ref="save-receipt-address"
                        buttonClass="billing-settings__col-button"
                        onClick={this.onClickReceiptAddressSave}
                        buttonSize="sm"
                        buttonColor="white"
                        buttonBorderColor="ocean-blue"
                        buttonTextColor="ocean-blue"
                        buttonHoverColor="ocean-blue"
                        buttonHoverBorderColor="ocean-blue"
                        buttonText={this.state.receiptAddressReadOnly
                          ? intl.formatMessage(messages.editAddressButton)
                          : intl.formatMessage(messages.saveButton)} />
                    </React.Fragment>
                  }
                  >
                    <ReceiptAddress
                      onChange={this.onChangeReceiptAddress}
                      receiptAddress={receiptAddress}
                      message={this.state.addressSavedMessage}
                      readOnly={this.state.receiptAddressReadOnly} />
                  </AdminSetting>
                </AdminSettingsSimpleRow>
              )}
            </AdminSettingsRowGroup>
          </React.Fragment>
        )}
        <HelloModal
          contentLabel={intl.formatMessage(messages.removeCard)}
          isOpen={this.state.showRemoveCardModal}
          onRequestClose={this.onRemoveCardModalRequestClose}
          buttons={[
            {
              type: 'secondary',
              text: intl.formatMessage(messages.nevermind),
              onClick: this.onRemoveCardModalRequestClose,
            },
            {
              type: 'primary',
              text: intl.formatMessage(messages.removeCard),
              onClick: this.onRemoveCard,
            },
          ]}
        >
          <p>
            <FormattedMessage
              id=""
              description="alert, billing settings page, asks user if user really wants to delete billing info"
              defaultMessage="Are you sure you want to remove your billing information from the system?" />
          </p>
        </HelloModal>
        { taxIdSupported && (
          <HelloModal
            contentLabel={intl.formatMessage(messages.updateTaxId, { taxCountry })}
            isOpen={this.state.showTaxIdModal}
            onRequestClose={this.onTaxIdModalRequestClose}
            buttons={[
              {
                type: 'secondary',
                text: intl.formatMessage(messages.cancelButton),
                onClick: this.onTaxIdModalRequestClose,
              },
              {
                type: 'primary',
                text: intl.formatMessage(messages.saveButton),
                onClick: this.onClickTaxIdSave,
                disabled: this.state.taxIdSaving,
              },
            ]}
          >
            { taxCountry in countries && (
              <div>
                {
                  intl.formatMessage(messages.taxIdTitle, { taxCountry })
                } ({intl.formatMessage(countries[taxCountry])})
              </div>
            )}
            <TaxId
              countryCode={taxCountry}
              onChange={this.onChangeTaxId} />
            <div className="hello-modal__error"><small>{this.state.taxIdErrorMessage}</small></div>
          </HelloModal>
        )}
        {billingPlans && this.state.showSwitchBillingPeriodModal ? (
          <SwitchBillingPeriodModal
            isOpen
            isTrialing={isFreeTrial}
            isMonthly={isMonthly}
            chargeAmount={altBillingPeriodCharge}
            startDate={altBillingPeriodStartDate}
            refund={altBillingPeriodRefund}
            planName={billingPlans[0].name}
            numTrialDays={numTrialDays}
            error={altBillingPeriodError}
            csrfToken={rest.app.csrfToken}
            onRequestClose={this.onSwitchBillingPeriodModalRequestClose}
            onSwitchBillingPeriodSuccess={this.onSwitchBillingPeriodSuccess}
            isTaxCollectionEnabled={isTaxCollectionEnabled} />
        ) : null}
      </div>
    );
  }
}

export default injectIntl(BillingSettings);
